// An accepted Find result, folded into the cached book so the page shows it at once. Pure, so
// `node --test` covers it. The server already holds the accepted match (and, for a playground
// person, stores the name for the next rebuild); this only saves a refetch.
//
// Every field is filled only where the person has none, the same rule the server's push uses.
import type { BookAccount, BookPayload, EnrichCandidate } from '../api/types.ts';
import { cleanName, isMaskedName } from './contactName.ts';

/** The accepted name wherever the person has none worth keeping: empty, only an address, or masked. */
function acceptedName(current: string | null | undefined, candidate: EnrichCandidate): string | null {
  const cleaned = cleanName(current);
  if (cleaned && !isMaskedName(cleaned)) return current ?? null;
  return candidate.name || current || null;
}

/** One account with the match applied to the contact, hero or playground person it names. */
function enrichedAccount(account: BookAccount, personId: string, candidate: EnrichCandidate): BookAccount {
  // A Salesforce contact (or a Scout find), by contact id.
  const contacts = account.contacts.map(contact => contact.id === personId
    ? {
        ...contact,
        email: contact.email ?? candidate.email,
        linkedin: contact.linkedin ?? candidate.linkedin_url,
        name: acceptedName(contact.name, candidate),
        title: contact.title ?? candidate.title,
      }
    : contact);

  // A playground person with no contact, by hero key (`playground:<address>`).
  const heroes = account.heroes?.map(hero => hero.key === personId && !hero.contact_id
    ? { ...hero, name: acceptedName(hero.name, candidate), title: hero.title ?? candidate.title, linkedin: hero.linkedin ?? candidate.linkedin_url }
    : hero);
  const playgroundEmail = personId.startsWith('playground:') ? personId.slice('playground:'.length).toLowerCase() : null;
  const playgroundPeople = account.playground_people?.map(person => person.email.toLowerCase() === playgroundEmail
    ? { ...person, name: acceptedName(person.name, candidate), title: person.title ?? candidate.title, linkedin: person.linkedin ?? candidate.linkedin_url }
    : person);

  return { ...account, contacts, heroes, playground_people: playgroundPeople };
}

/** The book with an accepted match applied wherever that person appears. */
export function applyEnrichedPerson(book: BookPayload, personId: string, candidate: EnrichCandidate): BookPayload {
  return { ...book, accounts: book.accounts.map(account => enrichedAccount(account, personId, candidate)) };
}
